<template>
  <div>
    <FormItem
      label="和其他实验："
      prop="experimentType"
      :rules="[
        {
          required: true,
          message: '请选择和其他实验的关系',
          trigger: 'change'
        }
      ]"
    >
      <Tooltip :disabled="!readonly" :content="`如需修改,请先点击'返回上一步'`">
        <RadioGroup
          v-model="formData.experimentType"
          type="button"
          button-style="solid"
        >
          <Radio label="0" :disabled="readonly">互斥</Radio>
          <Radio label="1" :disabled="readonly">并行</Radio>
        </RadioGroup>
      </Tooltip>
    </FormItem>

    <FormItem
      v-if="!isThirdPayCapabilitySelected"
      label="注册剧目："
      prop="playIdsUnlimited"
      :rules="[
        {
          required: true,
          trigger: 'change',
          validator(rule, value, callback) {
            if (
              formData.playIdsUnlimited !== 0 &&
              formData.playIdsUnlimited !== 1
            ) {
              callback('请选择注册剧目')
            } else {
              callback()
            }
          }
        }
      ]"
    >
      <Tooltip :disabled="!readonly" :content="`如需修改,请先点击'返回上一步'`">
        <RadioGroup
          v-model="formData.playIdsUnlimited"
          type="button"
          button-style="solid"
          :disabled="readonly"
          @on-change="
            () => {
              formData.playIds = []
              handleGetPlayBatch()
            }
          "
        >
          <Radio :label="0" :disabled="readonly">不限</Radio>
          <Radio :label="1" :disabled="readonly">指定剧目</Radio>
        </RadioGroup>
      </Tooltip>
    </FormItem>

    <FormItem
      v-if="!isThirdPayCapabilitySelected && formData.playIdsUnlimited"
      prop="playIds"
      :rules="[
        {
          message: '请选择注册剧目',
          trigger: 'change',
          validator(rule, value, callback) {
            if (!formData.playIds || formData.playIds.length === 0) {
              callback('请选择注册剧目')
            } else {
              callback()
            }
          }
        }
      ]"
    >
      <playCascader
        :disabled="readonly"
        style="width: 300px"
        :value="formData.playIds"
        @update:value="formData.playIds = $event"
        ref="playCnRef"
      />
    </FormItem>

    <FormItem v-if="!isThirdPayCapabilitySelected" label="推送人群">
      <Tooltip :disabled="!readonly" :content="`如需修改,请先点击'返回上一步'`">
        <RadioGroup
          v-model="formData.crowdMode"
          type="button"
          button-style="solid"
          :disabled="readonly"
          @on-change="handleCrowdModeChange"
        >
          <Radio label="all" :disabled="readonly || isRechargeTemplateCapabilitySelected">不限</Radio>
          <Radio label="selected" :disabled="readonly">选择人群包</Radio>
        </RadioGroup>
      </Tooltip>
    </FormItem>

    <FormItem
      v-if="formData.crowdMode === 'selected'"
      :label="isThirdPayCapabilitySelected ? '人群包' : ''"
      prop="crowdIds"
      :rules="[
        {
          message: '请选择人群包',
          trigger: 'change',
          validator(rule, value, callback) {
            if (!formData.crowdIds || !formData.crowdIds.length) {
              callback('请选择人群包')
            } else {
              callback()
            }
          }
        }
      ]"
    >
      <Select
        v-model="formData.crowdIds"
        placeholder="请输入人群包名称搜索"
        filterable
        multiple
        clearable
        :disabled="readonly"
        style="width: 300px"
      >
        <Option
          v-for="item in crowdOptions"
          :key="item.id"
          :value="item.id"
          :label="item.name"
        >
          {{ item.name }}
        </Option>
      </Select>
    </FormItem>
  </div>
</template>

<script>
import { getCrowdList } from '@/api/crowd'
import { getPlayBatch } from '@/api/play'
import { isThirdPayCapability, isRechargeTemplateCapability } from '../helpers'
import playCascader from '../../launch/play/components/playCascader.vue'

export default {
  name: 'ExperimentTargetFields',
  components: {
    playCascader
  },
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: () => ({
        groups: []
      })
    }
  },
  data() {
    return {
      crowdOptions: []
    }
  },
  computed: {
    isThirdPayCapabilitySelected() {
      return isThirdPayCapability(this.formData.experimentCapability)
    },
    isRechargeTemplateCapabilitySelected() {
      return isRechargeTemplateCapability(this.formData.experimentCapability)
    }
  },
  methods: {
    syncRechargeTemplateTargetMode() {
      if (!this.isRechargeTemplateCapabilitySelected) return

      if (this.formData.crowdMode === 'all') {
        this.$set(this.formData, 'crowdMode', 'selected')
        this.formData.crowdIds = []
      }
    },
    syncThirdPayTargetMode() {
      if (!this.isThirdPayCapabilitySelected) return

      this.$set(this.formData, 'playIdsUnlimited', '')
      this.$set(this.formData, 'playIds', [])
      this.$set(this.formData, 'crowdMode', 'selected')
      this.$set(this.formData, 'crowdType', 0)
    },
    getCrowdItemCountryCodes(item) {
      if (!item || typeof item !== 'object') return []

      const countryList =
        item.countryCodes ||
        item.countrycodes ||
        item.countryCodeList ||
        item.countryCodelist ||
        []

      return Array.isArray(countryList) ? countryList : []
    },
    syncSelectedCrowdCountries() {
      const selectedIds = Array.isArray(this.formData.crowdIds)
        ? this.formData.crowdIds.map((item) => String(item))
        : []

      const countryCodes = Array.from(
        new Set(
          this.crowdOptions
            .filter((item) => selectedIds.includes(String(item.id)))
            .reduce((acc, item) => {
              acc.push(...this.getCrowdItemCountryCodes(item))
              return acc
            }, [])
            .filter(Boolean)
        )
      )

      this.$set(this.formData, 'crowdCountryCodes', countryCodes)
    },
    async fetchCrowdOptions() {
      try {
        const params = this.isThirdPayCapabilitySelected
          ? { current: 1, size: 100000, usageScene: 5 }
          : { current: -1, size: -1 }
        const { data } = await getCrowdList(params)
        this.crowdOptions = data.records || []
        this.$set(this.formData, 'crowdOptions', this.crowdOptions)
        this.syncSelectedCrowdCountries()
      } catch (error) {
        this.$Message.error('获取人群包失败')
      }
    },
    handleGetPlayBatch() {
      if (!this.formData.playIds || !this.formData.playIds.length) return

      getPlayBatch({
        ids: this.formData.playIds.join(',')
      }).then((response) => {
        response.data.forEach((item) => {
          this.$refs.playCnRef.$refs.selectRef.onOptionClick({
            label: item.playName,
            value: item.id
          })
        })
      })
    },
    handleCrowdModeChange(value) {
      if (this.isThirdPayCapabilitySelected) {
        this.$set(this.formData, 'crowdMode', 'selected')
        return
      }

      if (value === 'all') {
        this.formData.crowdIds = []
        this.$set(this.formData, 'crowdCountryCodes', [])
      }
    }
  },
  created() {
    this.syncThirdPayTargetMode()
    this.syncRechargeTemplateTargetMode()
    this.fetchCrowdOptions()
  },
  watch: {
    'formData.experimentCapability'() {
      this.syncThirdPayTargetMode()
      this.syncRechargeTemplateTargetMode()
      this.fetchCrowdOptions()
    },
    'formData.crowdIds': {
      deep: true,
      handler() {
        this.syncSelectedCrowdCountries()
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (!this.isThirdPayCapabilitySelected && this.formData.playIdsUnlimited) {
        this.handleGetPlayBatch()
      }
    })
  }
}
</script>